// Based on githubs primer useSlots
// https://github.com/primer/react/blob/main/packages/react/src/hooks/useSlots.ts
import {
  Children,
  isValidElement,
  type ComponentPropsWithoutRef,
  type ReactElement,
  type ElementType as ReactElementType,
  type ReactNode,
} from 'react';

import { isDev } from '../utils/is-node-environment.js';

// slot config allows 2 options:
// 1. Component to match, example: { leadingVisual: LeadingVisual }
type ComponentMatcher = ReactElementType<Props>;
// Component to match + a configuration paramter to match multiple ones of the provided component
type ComponentArrayMatcher = { match: ComponentMatcher; multiple: true };

/**
 * @internal
 */
export type SlotConfig = Record<
  string,
  ComponentMatcher | ComponentArrayMatcher
>;

// We don't know what the props are yet, we set them later based on slot config
// oxlint-disable-next-line typescript/no-explicit-any
type Props = any;

type SlotElements<Config extends SlotConfig> = {
  [Property in keyof Config]: SlotValue<Config, Property>;
};

type SlotValue<
  Config,
  Property extends keyof Config,
> = Config[Property] extends { match: infer Match; multiple: true }
  ? Match extends ReactElementType
    ? Array<ReactElement<ComponentPropsWithoutRef<Match>, Match>>
    : // oxlint-disable-next-line typescript/no-explicit-any
      Match extends [infer ElementType extends ReactElementType, any]
      ? Array<ReactElement<ComponentPropsWithoutRef<ElementType>, ElementType>>
      : never
  : Config[Property] extends ReactElementType
    ? ReactElement<ComponentPropsWithoutRef<Config[Property]>, Config[Property]>
    : never;

/** Map the values of an object */
function mapValues<T extends Record<string, unknown>, V>(
  obj: T,
  fn: (value: T[keyof T]) => V,
) {
  return Object.keys(obj).reduce(
    (result, key: keyof T) => {
      result[key] = fn(obj[key]);
      return result;
    },
    {} as Record<keyof T, V>,
  );
}

/**
 * Extract components from `children` so we can render them in different places,
 * allowing us to implement components with SSR-compatible slot APIs.
 * Note: We can only extract direct children, not nested ones.
 * @internal
 */
export function useSlots<Config extends SlotConfig>(
  children: ReactNode,
  config: Config,
): [Partial<SlotElements<Config>>, ReactNode[]] {
  // Object mapping slot names to their elements
  // @ts-expect-error Check overlap with Array<never>
  const slots: Partial<SlotElements<Config>> = mapValues(
    config,
    (slotConfig) => {
      if (
        typeof slotConfig === 'object' &&
        'multiple' in slotConfig &&
        slotConfig.multiple === true
      ) {
        return [];
      }
      return undefined;
    },
  );

  // Array of elements that are not slots
  const rest: ReactNode[] = [];

  const keys = Object.keys(config) as Array<keyof Config>;
  const values = Object.values(config);

  Children.forEach(children, (child) => {
    // Ensure no empty elements are passed or rendered
    if (child === undefined || child === null || child === false) {
      return;
    }
    // If the child is not a valid element, push it immediately to the rest
    if (!isValidElement(child)) {
      rest.push(child);
      return;
    }
    // Find if the child matches one of the provided components.
    const index = values.findIndex((value) => {
      if (typeof value === 'object' && 'match' in value) {
        return child.type === value.match;
      }

      return child.type === value;
    });

    // If the child is not a slot, add it to the `rest` array
    if (index === -1) {
      rest.push(child);
      return;
    }

    const slotKey = keys[index];

    // If slot is already filled, ignore duplicates
    if (!Array.isArray(slots[slotKey]) && slots[slotKey]) {
      if (isDev()) {
        // oxlint-disable-next-line no-console
        console.log(
          `Found duplicate "${String(
            slotKey,
          )}" slot. Only the first will be rendered.`,
        );
      }

      return;
    }

    // If the child is a slot, add it to the `slots` object
    if (Array.isArray(slots[slotKey])) {
      slots[slotKey].push(child);
    } else {
      slots[slotKey] = child as SlotValue<Config, keyof Config>;
    }
  });

  return [slots, rest];
}
